import Link from 'next/link';
import type { ComponentProps } from 'react';

import { cn } from '../cn';
import { button, type ButtonVariants } from '../recipes/button';

export const isExternalHref = (href: string) => href.startsWith('http') || href.startsWith('mailto:');

/**
 * The anchor attributes an external href takes, for a Link that is not one of
 * the components below. Without `noreferrer` the destination learns the
 * referring origin, and an older browser lets it reach back through
 * window.opener.
 */
export const externalProps = (href: string) => (isExternalHref(href) ? { target: '_blank', rel: 'noreferrer' } : {});

/**
 * A call to action: a link marked with a destination glyph, → for the site
 * and ↗ for elsewhere. The marketing site's CTA links and the door cards both
 * take this shape.
 */
export type Cta = {
  label: string;
  href: string;
  /** Inert data attribute. The consuming app owns any analytics listener. */
  trackingId?: string;
  intent?: ButtonVariants['intent'];
  Icon?: React.ComponentType<React.ComponentProps<'svg'>>;
  /**
   * The mark's edge and its shadow. Spelled out at the call site because
   * Tailwind scans for whole class names, so one built from a hue name generates
   * nothing.
   */
  markClass?: string;
  /**
   * `outline` sets no colour of its own and draws its border from the current
   * one, so a single `text-*` here moves the label and the edge together.
   */
  className?: string;
};

type LinkProps = ComponentProps<typeof Link> & { href: string };

export const ButtonLink = ({
  href,
  intent,
  size,
  float,
  block,
  tone,
  className,
  ...props
}: ButtonVariants & LinkProps) => (
  <Link
    href={href}
    className={cn(button({ intent, size, float, block, tone }), className)}
    {...externalProps(href)}
    {...props}
  />
);

export const TextLink = ({ href, className, ...props }: LinkProps) => (
  <Link
    href={href}
    className={cn('link-underline font-medium text-periwinkle underline-offset-4', className)}
    {...externalProps(href)}
    {...props}
  />
);
